import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { CartService, CartItem } from '../../services/cart.service';
import { Observable, Subscription } from 'rxjs';
import { LanguageService } from '../../services/language.service';
import { AnalyticsService } from '../../services/analytics.service';
import { CurrencyService } from '../../services/currency.service';
import { OrderHistoryItem, OrderHistoryService, recentOrders } from '../../services/order-history.service';
import { OrderBarAppearance, orderBarAppearance, shouldShowOrderBar } from '../../shared/order-bar-visibility';

@Component({
    selector: 'app-floating-cart',
    templateUrl: './floating-cart.component.html',
    styleUrls: ['./floating-cart.component.scss'],
    standalone: false
})
export class FloatingCartComponent implements OnInit, OnDestroy {
  /**
   * Distance from the bottom edge, in px. The default reproduces the position this component has
   * always had, so the call site that passes nothing (`/category/:id`) is unchanged.
   *
   * The menu raises it so the cart rides above the attractions banner rather than the banner
   * dodging the cart. Bound as a custom property rather than `[style.bottom.px]`: an inline
   * `bottom` would beat the stylesheet and silently drop its `env(safe-area-inset-bottom)` term.
   */
  @Input() bottomInset = 20;

  /**
   * Whether `bottomInset` is measured from the display edge (so the safe-area inset must be added on
   * top) or from the top of something already covering it. False only when the cart is stacked on
   * the full-bleed attractions banner, which owns the home-indicator strip itself.
   */
  @Input() clearsSafeArea = true;

  cartItems$: Observable<CartItem[]>;

  /* Bound as ready-made CSS strings rather than via Angular's `.px` unit suffix: that suffix is
     specified for real style properties, not custom ones, so it is not worth relying on here. */
  get bottomInsetCss(): string {
    return `${this.bottomInset}px`;
  }

  get safeAreaMultiplier(): string {
    return this.clearsSafeArea ? '1' : '0';
  }
  cartTotal!: any;

  /** The cart's server sync is pending or in flight — see CartService.syncing$. */
  private cartSyncing = false;
  private cartSyncingSubscription?: Subscription;

  /**
   * Same signal as the order screen's button. The bar stays tappable — it only opens the
   * cart, and the cart's own order button is what waits for the sync.
   */
  get isSyncingCart(): boolean {
    return this.appearance === 'cart' && this.cartSyncing;
  }
  itemCount: number = 0;
  currencySymbol!: string;

  /**
   * Raw session history. The two-hour filter is applied in the getters below rather
   * than here, because it depends on the clock: an order aging out has to remove the
   * bar from a menu left open, and a value computed once at subscribe time never would.
   * Default change detection re-evaluates the getters, and the clientData poll already
   * ticks it.
   */
  private allOrderHistory: OrderHistoryItem[] = [];
  private orderHistorySubscription?: Subscription;

  get recentOrderCount(): number {
    return recentOrders(this.allOrderHistory, Date.now(), sessionStorage.getItem('clientId')).length;
  }

  /**
   * The bar is reachable for a live cart OR for an order recent enough to return to.
   * The second half is the change: a submitted order cleared the cart and left the
   * guest with no route back to what they had just ordered.
   */
  get shouldShow(): boolean {
    return shouldShowOrderBar(this.itemCount > 0, this.recentOrderCount > 0);
  }

  get appearance(): OrderBarAppearance {
    return orderBarAppearance(this.itemCount > 0);
  }

  /** A previous order has no live total to show — only the cart does. */
  get showsTotal(): boolean {
    return this.appearance === 'cart';
  }

  get badgeCount(): number {
    return this.appearance === 'cart' ? this.itemCount : this.recentOrderCount;
  }

  get labelKey(): string {
    return this.appearance === 'cart' ? 'floatingCart.cartText' : 'floatingCart.previousOrderText';
  }

  clientData: any = null;
  private currencySubscription?: Subscription;
  private refreshInterval?: any;
  private lastClientDataString: string = '';

  constructor(
    private cartService: CartService,
    private router: Router, 
    private languageService: LanguageService,
    private analytics: AnalyticsService,
    private currencyService: CurrencyService,
    private orderHistoryService: OrderHistoryService,
    private cdr: ChangeDetectorRef
  ) {
    this.cartItems$ = this.cartService.cartItems$;
  }

  ngOnInit(): void {
    this.cartItems$.subscribe(items => {
      this.itemCount = items.reduce((count, item) => count + item.quantity, 0);
    });
    this.cartService.total$.subscribe(total => {
      this.cartTotal = total;
    });

    this.cartSyncingSubscription = this.cartService.syncing$.subscribe(syncing => {
      this.cartSyncing = syncing;
    });
    this.orderHistorySubscription = this.orderHistoryService.orderHistory$.subscribe(history => {
      this.allOrderHistory = history;
    });
    
    // Load initial currency symbol from sessionStorage
    const cachedCurrency = sessionStorage.getItem('currencySymbol');
    if (cachedCurrency) {
      this.currencySymbol = cachedCurrency;
    }
    
    // Subscribe to currency changes (same pattern as menu component)
    this.currencySubscription = this.currencyService.currencySymbol$.subscribe(symbol => {
      this.currencySymbol = symbol;
      this.cdr.detectChanges();
    });
    
    // Load clientData for second currency checks
    this.loadClientData();

    // Set up periodic check for clientData updates (in case second currency settings change)
    this.refreshInterval = setInterval(() => {
      this.checkForClientDataUpdates();
    }, 1000);
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.currencySubscription) {
      this.currencySubscription.unsubscribe();
    }

    if (this.orderHistorySubscription) {
      this.orderHistorySubscription.unsubscribe();
    }

    this.cartSyncingSubscription?.unsubscribe();
    
    // Clear interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  private loadClientData(): void {
    const clientDataString = sessionStorage.getItem('clientData');
    if (clientDataString) {
      try {
        this.clientData = JSON.parse(clientDataString);
        this.lastClientDataString = JSON.stringify(this.clientData);
      } catch (e) {
        console.error('Error parsing clientData:', e);
      }
    }
  }

  private checkForClientDataUpdates(): void {
    const clientDataString = sessionStorage.getItem('clientData');
    if (clientDataString) {
      try {
        const newClientData = JSON.parse(clientDataString);
        const newClientDataString = JSON.stringify(newClientData);
        
        // If clientData has changed (e.g., second currency settings), update the UI
        if (newClientDataString !== this.lastClientDataString) {
          this.clientData = newClientData;
          this.lastClientDataString = newClientDataString;
          // Trigger change detection to update UI (same pattern as menu component)
          this.cdr.detectChanges();
        }
      } catch (error) {
        console.error('Error parsing clientData from sessionStorage:', error);
      }
    }
  }

  viewCart(): void {
    // Get current cart data for analytics
    const orderId = this.cartService.getCurrentOrderId();
    
    // Track cart opened event. `appearance` distinguishes a guest opening their live
    // cart from one returning to an order they already placed — the same tap, two very
    // different intents, and the funnel cannot tell them apart without it.
    this.analytics.log('cart_opened', {
      order_id: orderId,
      item_count: this.itemCount,
      appearance: this.appearance
    });
    
    this.router.navigate(['/order-details']);
  }

  getSecondPrice(price: number): string | null {
    return this.currencyService.getSecondPrice(price, this.clientData);
  }

  getSecondCurrencySymbol(): string | null {
    return this.currencyService.getSecondCurrencySymbol(this.clientData);
  }

  isSecondCurrencyActive(): boolean {
    return this.currencyService.isSecondCurrencyActive(this.clientData);
  }
} 